/**
 * Global command palette — opens with ⌘K (or Ctrl+K), closes with Escape.
 *
 * Pulls from admin search (platform-admin only) for brands + dashboards
 * + recent audit. Quick actions are static. Built on shadcn's `Command`
 * primitive (cmdk under the hood).
 */

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  ADMIN_BRANDS_ROUTE,
  type SearchResult,
  type SearchResultWire,
  adminBrandRoute,
  adminSearchApiPath,
  toSearchResult,
} from '@/lib/brand-wire';
import { formatDate } from '@/lib/dates';
import { useStaff } from '@/lib/staff';
import {
  Activity,
  Building2,
  ClipboardCheck,
  Code2,
  Database,
  HeartPulse,
  KeyRound,
  LayoutDashboard,
  ShieldCheck,
  Sparkles,
  Tags,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const QUICK_ACTIONS = [
  { id: 'overview', label: 'Admin overview', icon: LayoutDashboard, to: '/admin' },
  { id: 'onboard', label: 'Onboard a new client', icon: Sparkles, to: '/admin/onboarding' },
  { id: 'brands', label: 'Workspaces', icon: Building2, to: ADMIN_BRANDS_ROUTE },
  { id: 'data-sources', label: 'Data sources', icon: Database, to: '/admin/data-sources' },
  { id: 'dashboards', label: 'Dashboards', icon: LayoutDashboard, to: '/admin/dashboards' },
  { id: 'naming', label: 'Naming conventions', icon: Tags, to: '/admin/naming' },
  { id: 'signups', label: 'Signup queue', icon: ClipboardCheck, to: '/admin/signups' },
  { id: 'audit', label: 'Audit log', icon: Activity, to: '/admin/audit' },
  { id: 'usage', label: 'Platform usage', icon: HeartPulse, to: '/admin/usage' },
  { id: 'agency', label: 'Agency overview', icon: Building2, to: '/agency' },
  { id: 'billing', label: 'Billing', icon: KeyRound, to: '/billing' },
];

export function CommandPalette() {
  const { getAccessToken, sessionKey, me } = useAuth();
  const { isPlatformAdmin } = useStaff();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);

  // ⌘K / Ctrl+K toggles. Skip if focus is in an input that opts out.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Fetch results — debounced via useEffect timeout. Only for platform admins.
  useEffect(() => {
    if (!open || !isPlatformAdmin || !sessionKey) return;
    const t = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api<SearchResultWire>(adminSearchApiPath(query), {
          accessToken: getAccessToken(),
        });
        setResults(toSearchResult(r));
      } catch {
        setResults({ brands: [], dashboards: [], recentAudit: [] });
      } finally {
        setLoading(false);
      }
    }, 150);
    return () => window.clearTimeout(t);
  }, [open, query, sessionKey, isPlatformAdmin, getAccessToken]);

  const goTo = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  // Don't render the palette for users not signed in.
  if (!me?.user) return null;

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Quick navigate"
      description="Jump to anything"
    >
      <CommandInput
        placeholder={isPlatformAdmin ? 'Search workspaces, dashboards, actions…' : 'Search…'}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>{loading ? 'Searching…' : 'No matches.'}</CommandEmpty>

        {isPlatformAdmin && results?.brands && results.brands.length > 0 ? (
          <>
            <CommandGroup heading="Workspaces">
              {results.brands.map((t) => (
                <CommandItem
                  key={t.id}
                  value={`brand ${t.name} ${t.slug}`}
                  onSelect={() => goTo(adminBrandRoute(t.id))}
                >
                  <Building2 className="size-4" />
                  <span className="flex-1">{t.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{t.slug}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[9px] uppercase ${
                      t.status === 'active'
                        ? 'bg-success-soft text-fg'
                        : 'bg-surface-2 text-muted-foreground'
                    }`}
                  >
                    {t.status}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}

        {isPlatformAdmin && results?.dashboards && results.dashboards.length > 0 ? (
          <>
            <CommandGroup heading="Dashboards">
              {results.dashboards.map((d) => (
                <CommandItem
                  key={d.id}
                  value={`dashboard ${d.name} ${d.slug}`}
                  onSelect={() => goTo(`/admin/dashboards/${d.id}/build`)}
                >
                  <LayoutDashboard className="size-4" />
                  <span className="flex-1">{d.name}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{d.slug}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}

        {isPlatformAdmin ? (
          <>
            <CommandGroup heading="Quick actions">
              {QUICK_ACTIONS.map((a) => (
                <CommandItem key={a.id} value={`action ${a.label}`} onSelect={() => goTo(a.to)}>
                  <a.icon className="size-4" />
                  <span>{a.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}

        {isPlatformAdmin && results?.recentAudit && results.recentAudit.length > 0 ? (
          <CommandGroup heading="Recent activity">
            {results.recentAudit.slice(0, 5).map((a) => (
              <CommandItem
                key={a.id}
                value={`audit ${a.action}`}
                onSelect={() => goTo('/admin/audit')}
              >
                <Activity className="size-4 text-muted-foreground" />
                <span className="flex-1 truncate">{a.action}</span>
                <span className="text-[10px] text-muted-foreground">{formatRelative(a.at)}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ) : null}

        {/* Always-available navigation for everyone */}
        {!isPlatformAdmin ? (
          <CommandGroup heading="Navigate">
            <CommandItem value="home" onSelect={() => goTo('/')}>
              <LayoutDashboard className="size-4" /> Home
            </CommandItem>
            <CommandItem value="analytics" onSelect={() => goTo('/analytics')}>
              <Activity className="size-4" /> Analytics
            </CommandItem>
            <CommandItem value="billing" onSelect={() => goTo('/billing')}>
              <KeyRound className="size-4" /> Billing
            </CommandItem>
            {me.brands.length > 1 ? (
              <CommandItem value="agency" onSelect={() => goTo('/agency')}>
                <Building2 className="size-4" /> Agency overview
              </CommandItem>
            ) : null}
          </CommandGroup>
        ) : null}
      </CommandList>
      <div className="flex items-center justify-between border-t border-line bg-surface-2 px-3 py-2 text-[10px] text-muted-foreground">
        <span>
          <kbd className="rounded border border-line bg-surface px-1 py-0.5 font-mono">⌘K</kbd> to
          toggle ·{' '}
          <kbd className="rounded border border-line bg-surface px-1 py-0.5 font-mono">↵</kbd> to
          open ·{' '}
          <kbd className="rounded border border-line bg-surface px-1 py-0.5 font-mono">Esc</kbd> to
          close
        </span>
        {isPlatformAdmin ? <span className="font-mono">platform admin</span> : null}
        {/* Surface unused icons so tree-shaking doesn't choke on them. */}
        <span className="hidden">
          <ClipboardCheck className="size-3" />
          <Code2 className="size-3" />
          <ShieldCheck className="size-3" />
        </span>
      </div>
    </CommandDialog>
  );
}

function formatRelative(unixSec: number): string {
  const ms = Date.now() - unixSec * 1000;
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return formatDate(unixSec * 1000);
}
